import { z } from 'zod';

/**
 * AI-assist request/response contracts (v1 — editor toolbar, text tasks).
 *
 * All tasks hit ONE endpoint (`POST /api/admin/ai/assist`) discriminated by
 * `task`. They operate on transient text passed in the request (the post may
 * be unsaved), never on a stored post. The server resolves the provider
 * (Claude via AI Gateway by default), runs the task-specific prompt, then
 * parses + clamps the model output into the response shapes below.
 *
 * `suggest-category` needs no category list in the request — the server loads
 * the live (≤9) curated categories itself so the choice stays grounded.
 * `suggest-tags` returns NAMES ONLY; the FE matches them against existing tags
 * (`/api/admin/tags?q=`) and creates the missing ones on save via the normal
 * `tagIds` path — the endpoint never writes to the taxonomy tables.
 */

// Outer guards. The real per-request cap comes from the `ai.max_input_chars`
// setting, enforced server-side; these keep a hostile payload bounded.
const bodyText = z.string().max(40_000);
const titleText = z.string().min(1).max(500);

const rewriteTitleReq = z.object({
  task: z.literal('rewrite-title'),
  title: titleText,
  context: bodyText.optional(),
});
const titleVariantsReq = z.object({
  task: z.literal('title-variants'),
  title: titleText,
  context: bodyText.optional(),
});
const suggestSeoReq = z.object({
  task: z.literal('suggest-seo'),
  title: titleText,
  body: bodyText,
  summary: z.string().max(2_000).optional(),
});
const suggestTagsReq = z.object({
  task: z.literal('suggest-tags'),
  title: titleText,
  body: bodyText,
});
const suggestCategoryReq = z.object({
  task: z.literal('suggest-category'),
  title: titleText,
  body: bodyText,
});
const generateSummaryReq = z.object({
  task: z.literal('generate-summary'),
  title: titleText,
  body: bodyText,
});
const proofreadReq = z.object({
  task: z.literal('proofread'),
  // Plain text (title or body plaintext from the editor) — proofread does not
  // touch HTML markup, only the words.
  text: z.string().min(1).max(40_000),
});
const socialCaptionsReq = z.object({
  task: z.literal('social-captions'),
  title: titleText,
  body: bodyText,
  url: z.string().url().optional(),
});

export const aiAssistRequestSchema = z.discriminatedUnion('task', [
  rewriteTitleReq,
  titleVariantsReq,
  suggestSeoReq,
  suggestTagsReq,
  suggestCategoryReq,
  generateSummaryReq,
  proofreadReq,
  socialCaptionsReq,
]);

export type AiTask = z.infer<typeof aiAssistRequestSchema>['task'];
export type AiAssistRequest = z.infer<typeof aiAssistRequestSchema>;

/* ---------- Response shapes (server-built, discriminated by echoed task) ---------- */

export interface AiRewriteTitleResult {
  task: 'rewrite-title';
  title: string;
}
export interface AiTitleVariantsResult {
  task: 'title-variants';
  titles: string[];
}
export interface AiSuggestSeoResult {
  task: 'suggest-seo';
  metaTitle: string;
  metaDescription: string;
  focusKeyword: string;
}
export interface AiSuggestTagsResult {
  task: 'suggest-tags';
  tags: string[];
}
export interface AiSuggestCategoryResult {
  task: 'suggest-category';
  /** Empty string when the model can't confidently match one of the 9. */
  categorySlug: string;
  categoryName: string;
}
export interface AiGenerateSummaryResult {
  task: 'generate-summary';
  summary: string;
}
export interface AiProofreadChange {
  before: string;
  after: string;
  reason: string;
}
export interface AiProofreadResult {
  task: 'proofread';
  corrected: string;
  changes: AiProofreadChange[];
}
export interface AiSocialCaptionsResult {
  task: 'social-captions';
  facebook: string;
  x: string;
  line: string;
  hashtags: string[];
}

export type AiAssistResult =
  | AiRewriteTitleResult
  | AiTitleVariantsResult
  | AiSuggestSeoResult
  | AiSuggestTagsResult
  | AiSuggestCategoryResult
  | AiGenerateSummaryResult
  | AiProofreadResult
  | AiSocialCaptionsResult;

/** GET /api/admin/ai/status — drives FE button visibility (dark-ship). */
export interface AiStatus {
  enabled: boolean;
  provider: string;
  model: string;
  quota: { used: number; limit: number; resetAt: string };
}
